class KlaviyoBis extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.variantId = this.dataset.variantId;
    this.trigger = this.querySelector('[data-bis-trigger]');
    this.panel = this.querySelector('[data-bis-panel]');
    this.emailInput = this.querySelector('[data-bis-email]');
    this.submitButton = this.querySelector('[data-bis-submit]');
    this.messageEl = this.querySelector('[data-bis-message]');

    this.messages = {
      success: this.dataset.successMessage || '',
      error: this.dataset.errorMessage || '',
      invalid: this.dataset.invalidMessage || ''
    };

    this.trigger?.addEventListener('click', this.reveal.bind(this));
    this.submitButton?.addEventListener('click', this.subscribe.bind(this));
    this.emailInput?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.subscribe();
      }
    });
  }

  reveal() {
    if (!this.panel) return;
    this.trigger.hidden = true;
    this.panel.hidden = false;
    this.emailInput?.focus();
  }

  get publicKey() {
    const configured = window.theme && theme.klaviyo && theme.klaviyo.publicKey;
    if (configured) return configured;

    const scripts = document.querySelectorAll('script[src*="klaviyo.com/onsite/js/"]');
    for (const script of scripts) {
      try {
        const url = new URL(script.src);
        const companyId = url.searchParams.get('company_id');
        if (companyId) return companyId;

        const segments = url.pathname.split('/').filter(Boolean);
        const jsIndex = segments.indexOf('js');
        const candidate = jsIndex >= 0 ? segments[jsIndex + 1] : null;
        if (candidate && !candidate.endsWith('.js')) return candidate;
      } catch (error) {
        /* ignore malformed src */
      }
    }
    return null;
  }

  static isValidEmail(email) {
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
  }

  setMessage(text, state) {
    if (!this.messageEl) return;
    this.messageEl.textContent = text || '';
    this.messageEl.hidden = !text;
    if (state) {
      this.messageEl.dataset.state = state;
    } else {
      delete this.messageEl.dataset.state;
    }
  }

  setLoading(isLoading) {
    if (!this.submitButton) return;
    this.submitButton.disabled = isLoading;
    if (isLoading) {
      this.submitButton.setAttribute('aria-busy', 'true');
    } else {
      this.submitButton.removeAttribute('aria-busy');
    }
  }

  async subscribe() {
    const email = (this.emailInput?.value || '').trim();
    if (!KlaviyoBis.isValidEmail(email)) {
      this.setMessage(this.messages.invalid, 'error');
      this.emailInput?.focus();
      return;
    }

    const publicKey = this.publicKey;
    if (!publicKey || !this.variantId) {
      this.setMessage(this.messages.error, 'error');
      return;
    }

    const revision = (window.theme && theme.klaviyo && theme.klaviyo.revision) || '2026-04-15';

    this.setLoading(true);
    this.setMessage('', null);

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${encodeURIComponent(publicKey)}`,
        {
          method: 'POST',
          headers: {
            revision: revision,
            'Content-Type': 'application/vnd.api+json'
          },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                channels: ['EMAIL'],
                profile: {
                  data: {
                    type: 'profile',
                    attributes: { email: email }
                  }
                }
              },
              relationships: {
                variant: {
                  data: {
                    type: 'catalog-variant',
                    id: `$shopify:::$default:::${this.variantId}`
                  }
                }
              }
            }
          })
        }
      );

      if (response.ok || response.status === 202) {
        if (this.panel) this.panel.hidden = true;
        this.setMessage(this.messages.success, 'success');
      } else {
        this.setLoading(false);
        this.setMessage(this.messages.error, 'error');
      }
    } catch (error) {
      this.setLoading(false);
      this.setMessage(this.messages.error, 'error');
    }
  }
}

if (!customElements.get('klaviyo-bis')) {
  customElements.define('klaviyo-bis', KlaviyoBis);
}
